import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { ShieldCheck, Lock, Settings } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!code.trim()) {
      toast.error("Veuillez entrer le code d'accès");
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/admin/login`, { code });
      
      if (response.data.success) {
        sessionStorage.setItem("adminToken", response.data.token);
        toast.success("Connexion réussie");
        navigate("/admin/dashboard");
      } else {
        toast.error(response.data.message || "Code incorrect");
      }
    } catch (error) {
      toast.error("Erreur de connexion");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen mesh-gradient flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0F172A] rounded-2xl mb-4 shadow-lg">
            <Settings className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[#0F172A] tracking-tight">Administration</h1>
          <p className="text-[#64748B] mt-2 text-sm">
            FMI PAY - Panneau d'administration
          </p>
        </div>

        {/* Login Card */}
        <Card className="form-container border-0 shadow-xl" data-testid="admin-login-card">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2 text-[#2563EB]">
              <Lock className="w-4 h-4" />
              <span className="text-xs font-medium uppercase tracking-wider">
                Accès sécurisé
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code" className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
                  Code d'accès administrateur
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                  <Input
                    id="code"
                    type="password"
                    placeholder="••••••••"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className="pl-10 h-10 text-sm"
                    data-testid="admin-code-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-[#0F172A] hover:bg-[#1E293B] text-white font-medium rounded-lg transition-all shadow-md hover:shadow-lg"
                disabled={isLoading}
                data-testid="admin-login-button"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Connexion...
                  </span>
                ) : (
                  "Accéder au panneau"
                )}
              </Button>

              <button
                type="button"
                onClick={() => navigate("/")}
                className="w-full text-sm text-[#64748B] hover:text-[#0F172A]"
              >
                Retour à l'accueil
              </button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Security Footer */}
      <div className="mt-8 pb-6 text-center">
        <div className="flex items-center justify-center gap-3">
          <div className="security-badge">
            <ShieldCheck className="w-4 h-4" />
            <span>Accès sécurisé</span>
          </div>
        </div>
      </div>
    </div>
  );
}
